// DivingCredentialsComponent.jsx
import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Grid,
  Paper,
  Alert,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  CardMembership,
  Waves,
  LocationOn,
  Print,
  Share,
  Refresh,
} from "@mui/icons-material";

// Import custom hooks
import { useAuth } from "../contexts/AuthContext"; // Adjust path as needed for your AuthContext
import { useDivingData } from "../hooks/useDivingData"; // Adjust path as needed for your hooks
import { useDivingReports } from "../hooks/useDivingReports"; // Adjust path as needed for your hooks

const DivingCredentialsComponent = () => {
  const [previewOpen, setPreviewOpen] = useState(false);

  // Consume data and functions from custom hooks
  const { currentUser, loading: authLoading } = useAuth();
  const {
    certifications,
    recentDives,
    loading,
    error,
    fetchDivingData,
    apiClient,
    userObjectId,
  } = useDivingData();
  const {
    shareUrl,
    shareLoading,
    generateShareableLink,
    getUniqueLocations,
    groupedDives,
    formatDate,
  } = useDivingReports(
    certifications,
    recentDives,
    userObjectId, // Pass userObjectId from useDivingData (or useAuth directly if needed)
    apiClient
  );

  // Show loading if authentication is still processing
  if (authLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
        sx={{
          background: "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)",
          borderRadius: 2,
          m: 2,
        }}
      >
        <Box textAlign="center">
          <CircularProgress size={60} thickness={4} />
          <Typography variant="h6" sx={{ mt: 2, color: "primary.main" }}>
            Authenticating...
          </Typography>
        </Box>
      </Box>
    );
  }

  // Show loading for diving data or errors
  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
        sx={{
          background: "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)",
          borderRadius: 2,
          m: 2,
        }}
      >
        <Box textAlign="center">
          <CircularProgress size={60} thickness={4} />
          <Typography variant="h6" sx={{ mt: 2, color: "primary.main" }}>
            Loading diving credentials...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        sx={{ m: 2 }}
        action={
          <Button color="inherit" size="small" onClick={fetchDivingData}>
            Retry
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", p: 3 }}>
      {/* Header */}
      <Paper
        elevation={4}
        sx={{
          p: 4,
          mb: 3,
          background: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
          color: "white",
          borderRadius: 3,
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Box>
            <Typography variant="h4" gutterBottom fontWeight="bold">
              🤿 Diving Credentials Summary
            </Typography>
            <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
              Diving credential and recent activity report
            </Typography>
            <Typography
              variant="caption"
              sx={{ opacity: 0.8, display: "block", mt: 1 }}
            >
              Generated on {new Date().toLocaleDateString()}
            </Typography>
            {currentUser && (
              <Typography
                variant="caption"
                sx={{ opacity: 0.8, display: "block" }}
              >
                User: {currentUser.email}
              </Typography>
            )}
          </Box>
          <Box>
            <Tooltip title="Refresh Data">
              <IconButton
                onClick={fetchDivingData}
                sx={{ color: "white", mr: 1 }}
              >
                <Refresh />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Box sx={{ mt: 3 }}>
          {/* <Button
            variant="contained"
            color="secondary"
            startIcon={
              shareLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <Share />
              )
            }
            onClick={generateShareableLink}
            disabled={shareLoading}
            sx={{ mr: 2, fontWeight: "bold" }}
          >
            {shareLoading ? "Generating Link..." : "Share with Dive Center"}
          </Button> */}
          <Button
            variant="outlined"
            startIcon={<Print />}
            onClick={() => setPreviewOpen(true)}
            sx={{
              color: "white",
              borderColor: "white",
              "&:hover": {
                borderColor: "rgba(255,255,255,0.8)",
                backgroundColor: "rgba(255,255,255,0.1)",
              },
            }}
          >
            Print Preview
          </Button>
          {shareUrl && (
            <Typography variant="body2" sx={{ mt: 2, opacity: 0.9 }}>
              Share URL: {shareUrl}
            </Typography>
          )}
        </Box>
      </Paper>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6}>
          <Card
            elevation={3}
            sx={{
              textAlign: "center",
              transition: "transform 0.2s",
              "&:hover": { transform: "translateY(-4px)" },
              borderRadius: 2,
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <CardMembership color="primary" sx={{ fontSize: 48, mb: 2 }} />
              <Typography variant="h3" color="primary" fontWeight="bold">
                {certifications.length}
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                fontWeight="medium"
              >
                Active Certifications
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Card
            elevation={3}
            sx={{
              textAlign: "center",
              transition: "transform 0.2s",
              "&:hover": { transform: "translateY(-4px)" },
              borderRadius: 2,
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Waves color="primary" sx={{ fontSize: 48, mb: 2 }} />
              <Typography variant="h3" color="primary" fontWeight="bold">
                {recentDives.length}
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                fontWeight="medium"
              >
                Total Dives (Last 12 Months)
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Certifications */}
        <Grid item xs={12} lg={6}>
          <Card elevation={3} sx={{ borderRadius: 2, height: "fit-content" }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" mb={2}>
                <CardMembership
                  color="primary"
                  sx={{ mr: 1.5, fontSize: 28 }}
                />
                <Typography variant="h5" fontWeight="bold">
                  Diving Certifications
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              {certifications.length === 0 ? (
                <Box textAlign="center" py={4}>
                  <Typography color="text.secondary" variant="h6">
                    No certifications on file
                  </Typography>
                  <Typography
                    color="text.secondary"
                    variant="body2"
                    sx={{ mt: 1 }}
                  >
                    Add your certifications to display them here
                  </Typography>
                </Box>
              ) : (
                <List sx={{ p: 0 }}>
                  {certifications.map((cert, index) => (
                    <ListItem
                      key={index}
                      sx={{
                        px: 0,
                        py: 1.5,
                        borderBottom:
                          index < certifications.length - 1
                            ? "1px solid #f0f0f0"
                            : "none",
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <CardMembership color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={1}
                            mb={0.5}
                          >
                            <Typography variant="subtitle1" fontWeight="medium">
                              {cert.certificationId?.agency} -{" "}
                              {cert.certificationId?.name || "Certification"}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              {cert?.certificationNumber || "Not Available"}{" "}
                              &bull; Issued:{" "}
                              {formatDate(cert.certificationDate)}
                            </Typography>
                            {cert.certificationId?.organization && (
                              <Typography
                                variant="body2"
                                color="primary"
                                fontWeight="medium"
                              >
                                {cert.certificationId.organization}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Diving Activity (Grouped by Site) */}
        <Grid item xs={12} lg={6}>
          <Card elevation={3} sx={{ borderRadius: 2, height: "fit-content" }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" mb={2}>
                <Waves color="primary" sx={{ mr: 1.5, fontSize: 28 }} />
                <Typography variant="h5" fontWeight="bold">
                  1 Year Diving Activity (Grouped by Site)
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              {groupedDives.length === 0 ? (
                <Box textAlign="center" py={4}>
                  <Typography color="text.secondary" variant="h6">
                    No recent dives recorded
                  </Typography>
                  <Typography
                    color="text.secondary"
                    variant="body2"
                    sx={{ mt: 1 }}
                  >
                    Your dives from the last 12 months will appear here
                  </Typography>
                </Box>
              ) : (
                <List sx={{ p: 0, maxHeight: 500, overflow: "auto" }}>
                  {groupedDives.slice(0, 15).map((groupedDive, index) => (
                    <ListItem
                      key={`${groupedDive.siteName}-${groupedDive.date}`}
                      sx={{
                        px: 0,
                        py: 1.5,
                        borderBottom:
                          index < Math.min(groupedDives.length, 15) - 1
                            ? "1px solid #f0f0f0"
                            : "none",
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <LocationOn color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                          >
                            <Typography variant="subtitle1" fontWeight="medium">
                              {groupedDive.siteName}
                            </Typography>
                            {groupedDive.count > 1 && (
                              <Chip
                                label={`${groupedDive.count} Dives`}
                                size="small"
                                color="primary"
                                variant="outlined" // Outlined chip for visual distinction
                                sx={{ ml: 1, fontWeight: "bold" }}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              {groupedDive.location.city || "Unknown City"}
                              {groupedDive.location.province
                                ? `, ${groupedDive.location.province}`
                                : ""}
                              {groupedDive.location.country
                                ? `, ${groupedDive.location.country}`
                                : ""}
                              <br />
                              {groupedDive.date}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                  {groupedDives.length > 15 && (
                    <ListItem sx={{ px: 0, justifyContent: "center" }}>
                      <Typography
                        variant="body2"
                        color="primary"
                        fontWeight="medium"
                      >
                        +{groupedDives.length - 15} more unique dive days...
                      </Typography>
                    </ListItem>
                  )}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Print Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h5" fontWeight="bold">
            🤿 Diving Credentials Report
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Credential and activity summary for dive centers and training
            organizations
          </Typography>
          {currentUser && (
            <Typography
              variant="caption"
              sx={{ opacity: 0.8, display: "block", mt: 0.5 }}
            >
              For: {currentUser.email}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ borderRadius: 1 }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "primary.main" }}>
                  <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                    Credential Summary
                  </TableCell>
                  <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                    Details
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow hover>
                  <TableCell sx={{ fontWeight: "medium" }}>
                    Total Certifications
                  </TableCell>
                  <TableCell>{certifications.length}</TableCell>
                </TableRow>
                <TableRow hover>
                  <TableCell sx={{ fontWeight: "medium" }}>
                    Recent Dives (12 months)
                  </TableCell>
                  <TableCell>{recentDives.length}</TableCell>
                </TableRow>
                <TableRow hover>
                  <TableCell sx={{ fontWeight: "medium" }}>
                    Dive Locations
                  </TableCell>
                  <TableCell>
                    {getUniqueLocations().slice(0, 5).join("; ")}
                    {getUniqueLocations().length > 5 ? "..." : ""}
                  </TableCell>
                </TableRow>
                <TableRow hover>
                  <TableCell sx={{ fontWeight: "medium" }}>
                    Certifications List
                  </TableCell>
                  <TableCell>
                    {certifications.length > 0 ? (
                      <List dense disablePadding>
                        {certifications.map((cert, index) => (
                          <ListItem key={index} disablePadding>
                            <ListItemText
                              primary={`${cert.certificationId?.agency} - ${
                                cert.certificationId?.name || "Certification"
                              } (No: ${cert.certificationNumber || "N/A"})`}
                            />
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      "No certifications to display."
                    )}
                  </TableCell>
                </TableRow>
                <TableRow hover>
                  <TableCell sx={{ fontWeight: "medium" }}>
                    Report Generated
                  </TableCell>
                  <TableCell>{new Date().toLocaleDateString()}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
          <Button
            onClick={() => window.print()}
            variant="contained"
            startIcon={<Print />}
          >
            Print Report
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DivingCredentialsComponent;
